import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useBuilding } from "../../context/BuildingContext";
import { formatBuildingAddress } from "../../services/buildingService";

export default function BuildingJumper() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { currentBuilding, buildings, selectBuilding } = useBuilding();

  const filteredBuildings = useMemo(() => {
    if (!searchQuery.trim()) return buildings;
    const query = searchQuery.toLowerCase();
    return buildings.filter(
      (building) =>
        building.name.toLowerCase().includes(query) ||
        building.street_name.toLowerCase().includes(query) ||
        building.city.toLowerCase().includes(query)
    );
  }, [buildings, searchQuery]);

  const showSearch = buildings.length > 5;

  function toggleDropdown() {
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchQuery("");
    }
  }

  function closeDropdown() {
    setIsOpen(false);
    setSearchQuery("");
  }

  function handleBuildingSelect(building: typeof buildings[0]) {
    selectBuilding(building);
    closeDropdown();
    navigate(`/sites/${building.id}`);
  }

  if (!currentBuilding) {
    return (
      <Link
        to="/sites"
        className="flex items-center gap-2 px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <svg
          className="h-5 w-5 text-gray-500 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <span className="font-medium text-theme-sm">Select Building</span>
      </Link>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-2 px-3 py-2 text-gray-700 rounded-lg dropdown-toggle hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <svg
          className="h-5 w-5 text-gray-500 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <span className="font-medium text-theme-sm max-w-[200px] truncate">
          {currentBuilding.name}
        </span>
        <svg
          className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute left-0 mt-[10px] w-[320px] flex-col rounded-2xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark overflow-hidden"
      >
        {showSearch && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-800">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search buildings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>
        )}

        <div className="max-h-[300px] overflow-y-auto">
          {filteredBuildings.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No buildings found
            </div>
          ) : (
            <ul className="py-2">
              {filteredBuildings.map((building) => {
                const isSelected = currentBuilding?.id === building.id;
                return (
                  <li key={building.id}>
                    <button
                      onClick={() => handleBuildingSelect(building)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 ${
                        isSelected ? "bg-brand-50 dark:bg-brand-500/10" : ""
                      }`}
                    >
                      <div
                        className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${
                          isSelected
                            ? "bg-brand-500"
                            : "bg-transparent"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium text-sm truncate ${
                            isSelected
                              ? "text-brand-600 dark:text-brand-400"
                              : "text-gray-800 dark:text-white/90"
                          }`}
                        >
                          {building.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {formatBuildingAddress(building)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Dropdown>
    </div>
  );
}
